import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import saveAs from "file-saver";
import { useState } from "react";
import { useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { ssoApi } from "@entrilia/shared/api/sso";
import type { ApiError } from "@entrilia/shared/api/types";
import { getAuthSettings } from "@entrilia/shared/auth/settings";
import PoweredBy from "@entrilia/shared/components/PoweredBy";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { authCookieExists } from "@entrilia/shared/utilities/cookies";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { combineValidators, emailValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import { accessCheckApi } from "../../api/accessCheckApi";
import type { AuthChallengeOutcome } from "../../api/types/accessCheckApiTypes";
import { userEmailSelector } from "../../store/state/user/selectors";
import AccessCheckBody from "./AccessCheckBody";
import type { AccessCheckStepKind, VerifyResult } from "./accessCheckState";
import {
  collectingEmailAction,
  diagnosticsFailedAction,
  enteringCodeAction,
  finishResendingAction,
  finishVerifyingAction,
  initialAccessCheckState,
  requestCodeFailedAction,
  requestingCodeAction,
  resetAccessCheckAction,
  setEmailFieldErrorAction,
  showResultsAction,
  startChecksAction,
  startResendingAction,
  startVerifyingAction,
  updateEmailDraftAction,
} from "./accessCheckState";
import {
  emitAccessCheckCodeEntered,
  emitAccessCheckCodeRequested,
  emitAccessCheckCompleted,
  emitAccessCheckReportDownloaded,
  emitAccessCheckStarted,
} from "./accessCheckTelemetry";
import { buildAccessCheckReport } from "./buildAccessCheckReport";
import { classifyAccessCheckResult } from "./classifyAccessCheckResult";
import { runBlobProbes, runConnectionChecks } from "./runConnectionChecks";
import type { ConnectionCheckResult } from "./types";

const titleByStep: Record<AccessCheckStepKind, string> = {
  initial: "You do not have access",
  "running-checks": "Access check",
  "collecting-email": "Access check",
  "requesting-code": "Access check",
  "entering-code": "Access check",
  verifying: "Access check",
  "show-results": "Access check completed",
  "diagnostics-failed": "Access check",
};

const codeErrorByOutcome: Record<AuthChallengeOutcome, string> = {
  Valid: "",
  Missing: "Please enter the verification code we emailed you.",
  Expired: "This code has expired. Please request a new one.",
  Mismatch: "This code is incorrect. Please try again or request a new one.",
  AttemptsExceeded: "Too many attempts. Please request a new code.",
};

const requestCodeErrorMessage = "We couldn't send a verification code. Please try again.";
const resendCodeErrorMessage = "We couldn't send a new code. Please try again.";
const genericVerifyErrorMessage = "We couldn't verify your code. Please try again.";

const requestCode = withErrorHandling(accessCheckApi.requestCode);
const checkAccess = withErrorHandling(accessCheckApi.checkAccess);
const getBlobProbes = withErrorHandling(accessCheckApi.getBlobProbes);

const logOut = withErrorHandling(ssoApi.logout);

const mapAuthErrorToVerifyResult = (apiError: ApiError | undefined): VerifyResult => {
  if (apiError?.type === "AuthCodeAttemptsExhausted") {
    return { kind: "invalid", outcome: "AttemptsExceeded" };
  }
  if (apiError?.type === "InvalidAuthCode") {
    return { kind: "invalid", outcome: "Mismatch" };
  }
  return { kind: "error" };
};

const verifyAccessRequest = async (email: string, code: string): Promise<VerifyResult> => {
  const [data, error] = await checkAccess(email, code);
  if (error) {
    const result = mapAuthErrorToVerifyResult(error);
    if (result.kind === "error") {
      logError(error, "[AccessCheck] checkAccess");
    }
    return result;
  }
  return { kind: "valid", hasAccess: Boolean(data.hasInvestorPortalAccess) };
};

const validateEmail = combineValidators(requiredValidator, emailValidator);

// Probes the document-storage SAS URLs returned by the access-check endpoint.
// Treat any failure here as "no extra signal" — the network checks above are the
// load-bearing diagnostics; blob probes are additive coverage for IT-blocked
// document hosts. On API failure (e.g., 401 if the code expired between calls)
// we silently return an empty list so the verify flow keeps progressing.
const runBlobChecksForVerifiedCode = async (email: string, code: string): Promise<ConnectionCheckResult[]> => {
  const [targets, error] = await getBlobProbes(email, code);
  if (error) {
    logError(error, "[AccessCheck] getBlobProbes");
    return [];
  }
  if (targets.length === 0) {
    return [];
  }
  try {
    return await runBlobProbes(targets);
  } catch (probeError) {
    logError(probeError, "[AccessCheck] runBlobProbes");
    return [];
  }
};

const AccessCheck = () => {
  const userEmail = useSelector(userEmailSelector);
  const [state, setState] = useState(initialAccessCheckState);

  const sendCodeAndAdvance = async (checks: ConnectionCheckResult[], email: string) => {
    setState(requestingCodeAction(checks, email));
    const [, error] = await requestCode(email);
    emitAccessCheckCodeRequested({ phase: "initial", success: !error, email, errorMessage: error?.message });
    if (error) {
      logError(error, "[AccessCheck] requestCode");
      setState(requestCodeFailedAction(checks, email, requestCodeErrorMessage));
      return;
    }
    setState(enteringCodeAction());
  };

  const handleStartChecks = async () => {
    setState(startChecksAction);
    let checks: ConnectionCheckResult[];
    try {
      checks = await runConnectionChecks();
    } catch (error) {
      // Diagnostics themselves crashed (e.g., unsupported browser API). Don't
      // route through classifyAccessCheckResult — an empty checks array would
      // resolve to bucket 2 (offline) and mislead the user. Show a dedicated
      // failure state instead.
      logError(error, "[AccessCheck] runConnectionChecks");
      setState(diagnosticsFailedAction);
      return;
    }
    if (checks.length === 0) {
      setState(diagnosticsFailedAction);
      return;
    }
    const failed = checks.filter((c) => !c.ok);
    emitAccessCheckStarted({
      passed: failed.length === 0,
      failedCount: failed.length,
      failedIds: failed.map((c) => c.id).join(","),
      email: userEmail || undefined,
    });
    if (failed.length > 0) {
      const classification = classifyAccessCheckResult({ checks });
      emitAccessCheckCompleted({ classification, email: userEmail || undefined });
      setState(showResultsAction(checks, classification));
      return;
    }

    if (userEmail) {
      await sendCodeAndAdvance(checks, userEmail);
    } else {
      setState(collectingEmailAction(checks));
    }
  };

  const handleEmailDraftChange = (value: string) => {
    setState(updateEmailDraftAction(value));
  };

  const handleEmailSubmit = async () => {
    if (state.step !== "collecting-email") {
      return;
    }
    const email = sanitizeEmail(state.emailDraft);
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setState(setEmailFieldErrorAction(validation.error));
      return;
    }
    await sendCodeAndAdvance(state.checks, email);
  };

  const handleResendCode = async () => {
    if (state.step !== "entering-code") {
      return;
    }
    setState(startResendingAction);
    const [, error] = await requestCode(state.email);
    emitAccessCheckCodeRequested({
      phase: "resend",
      success: !error,
      email: state.email,
      errorMessage: error?.message,
    });
    if (error) {
      logError(error, "[AccessCheck] resend requestCode");
      setState(finishResendingAction(resendCodeErrorMessage));
      return;
    }
    setState(finishResendingAction());
  };

  const handleVerifySubmit = async (code: string) => {
    if (state.step !== "entering-code") {
      return;
    }
    const { email } = state;
    setState(startVerifyingAction);
    const result = await verifyAccessRequest(email, code);
    emitAccessCheckCodeEntered({
      outcome: result.kind === "valid" ? "Valid" : result.kind === "invalid" ? result.outcome : "Error",
      email,
      hasInvestorPortalAccess: result.kind === "valid" ? result.hasAccess : undefined,
    });
    if (result.kind !== "valid") {
      setState(finishVerifyingAction(result));
      return;
    }
    if (!result.hasAccess) {
      // Skip blob probes — user has no IR portal access. Probes would target
      // storage they can't reach anyway, and any failure would falsely classify
      // them as bucket 3 (network blocked) instead of bucket 4 (no access).
      setState(finishVerifyingAction(result));
      return;
    }
    const blobChecks = await runBlobChecksForVerifiedCode(email, code);
    setState(finishVerifyingAction(result, blobChecks));
  };

  const handleVerifyDone = () => {
    if (state.step !== "verifying") {
      return;
    }
    const { checks, email, verifyResult } = state;
    if (verifyResult?.kind === "valid") {
      const classification = classifyAccessCheckResult({ checks, hasAccess: verifyResult.hasAccess, email });
      emitAccessCheckCompleted({ classification, email, hasInvestorPortalAccess: verifyResult.hasAccess });
      setState(showResultsAction(checks, classification, { email, hasInvestorPortalAccess: verifyResult.hasAccess }));
      return;
    }
    if (verifyResult?.kind === "invalid") {
      setState(enteringCodeAction(codeErrorByOutcome[verifyResult.outcome]));
      return;
    }
    setState(enteringCodeAction(genericVerifyErrorMessage));
  };

  const handleDownload = () => {
    if (state.step !== "show-results" || !state.classification) {
      return;
    }
    const report = buildAccessCheckReport({
      checks: state.checks,
      classification: state.classification,
      email: state.email || userEmail || undefined,
      hasInvestorPortalAccess: state.hasInvestorPortalAccess,
    });
    saveAs(report.blob, report.filename);
    emitAccessCheckReportDownloaded({
      classification: state.classification,
      email: state.email || userEmail || undefined,
      hasInvestorPortalAccess: state.hasInvestorPortalAccess,
    });
  };

  const handleResetToInitial = () => {
    setState(resetAccessCheckAction);
  };

  const handleLogInClick = async () => {
    if (authCookieExists()) {
      const { userFlowSignIn, cookieTarget } = getAuthSettings();
      const [, error] = await logOut({ userFlow: userFlowSignIn, cookieTarget });
      if (error) {
        logError(error, "[AccessCheck] logOut");
      }
    }

    window.location.href = "/";
  };

  return (
    <Paper variant="elevation" sx={{ width: { xs: "calc(100vw - 32px)", sm: 552 } }}>
      <Stack spacing={2.5} sx={{ px: 4, py: 3, alignItems: "center" }}>
        {state.step === "initial" && <WarningDialogIcon sx={{ width: 80, height: 80 }} />}

        <Typography variant="h6">{titleByStep[state.step]}</Typography>

        {userEmail && (
          <Typography>
            <Typography component="span" color="textSecondary">
              You are signed in as{" "}
            </Typography>
            {userEmail}
          </Typography>
        )}

        <AccessCheckBody
          state={state}
          onStartChecks={handleStartChecks}
          onEmailDraftChange={handleEmailDraftChange}
          onEmailSubmit={handleEmailSubmit}
          onResendCode={handleResendCode}
          onVerifySubmit={handleVerifySubmit}
          onVerifyDone={handleVerifyDone}
          onDownload={handleDownload}
          onResetToInitial={handleResetToInitial}
        />

        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography color="textSecondary">Want to try another account?</Typography>
          <Button variant="text" color="primary" sx={{ minWidth: "auto" }} onClick={handleLogInClick}>
            Log in
          </Button>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <PoweredBy />
        </Box>
      </Stack>
    </Paper>
  );
};

export default AccessCheck;
